/**
 * Derive a 12-month YearCalendar cell array from a HerbicideTimingResult.
 * Pre-emergent window months get the primary fill, post-emergent window
 * months get the secondary fill, the rest are inactive. `bestMonth`
 * defaults to the start month of the pre-emergent window (or post if
 * pre is absent), which the calendar highlights with a ring.
 */

import {
  parseWindowStartDate,
  parseWindowEndDate,
  type HerbicideTimingResult,
} from "./herbicide-timing-calculator"
import type { YearCalendarCell } from "./year-calendar-types"

const H_PRE = "#16a34a" // green-600 (act now)
const H_POST = "#dcfce7" // green-100 (ambient)
const H_OFF = "#f9fafb" // gray-50

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export interface HerbicideCalendar {
  cells: YearCalendarCell[]
  bestMonth?: number
  hasPre: boolean
  hasPost: boolean
}

function monthsCovered(start: Date | null, end: Date | null): Set<number> {
  const set = new Set<number>()
  if (!start || !end) return set
  if (end.getTime() < start.getTime()) return set
  const d = new Date(start.getFullYear(), start.getMonth(), 1)
  const endD = new Date(end.getFullYear(), end.getMonth(), 1)
  // Bail if the window spans more than 18 months (parser produced something nonsensical).
  let safety = 0
  while (d.getTime() <= endD.getTime() && safety < 18) {
    set.add(d.getMonth())
    d.setMonth(d.getMonth() + 1)
    safety++
  }
  return set
}

function parseSafely(windowStr: string | undefined): {
  start: Date | null
  end: Date | null
} {
  if (!windowStr) return { start: null, end: null }
  return {
    start: parseWindowStartDate(windowStr),
    end: parseWindowEndDate(windowStr),
  }
}

export function buildHerbicideCalendar(
  result: HerbicideTimingResult,
): HerbicideCalendar | null {
  const pre = parseSafely(result.preEmergentTiming?.optimalWindow)
  const post = parseSafely(result.postEmergentTiming?.optimalWindow)

  const preMonths = monthsCovered(pre.start, pre.end)
  const postMonths = monthsCovered(post.start, post.end)

  if (preMonths.size === 0 && postMonths.size === 0) return null

  const cells: YearCalendarCell[] = MONTH_LABELS.map((label, i) => {
    if (preMonths.has(i)) return { i, label, fill: H_PRE }
    if (postMonths.has(i)) return { i, label, fill: H_POST }
    return { i, label, fill: H_OFF }
  })

  const bestMonth =
    pre.start !== null
      ? pre.start.getMonth()
      : post.start !== null
        ? post.start.getMonth()
        : undefined

  return {
    cells,
    bestMonth,
    hasPre: preMonths.size > 0,
    hasPost: postMonths.size > 0,
  }
}
